import { useState, useEffect, useRef } from "react";
import { Star, ChevronLeft, ChevronRight, ExternalLink, BadgeCheck } from "lucide-react";

// Easily editable — real values from Ménage KGD Google Business Profile
export const GOOGLE_URL = "https://share.google/r6SMqGnlOjFceqtAm";
export const GOOGLE_RATING = "4,9/5";
export const GOOGLE_COUNT = 14;

// Real verified Google reviews — DO NOT invent
const REVIEWS = [
    {
        name: "Kathleen B.",
        stars: 5,
        text: "Très satisfaite de la qualité du service offert par Katarina et en plus, elle est très gentille ! Elle m'a été référée par une amie ce qui m'a donné confiance. C'est une petite entreprise qui mérite d'être encouragée !",
    },
    {
        name: "Steve T.",
        stars: 5,
        text: "Attentive aux détails, toujours à l'heure. Elle se soucie du client en gardant une bonne communication et s'adapte à toutes demandes, nous recommandons Ménage KGD.",
    },
    {
        name: "Laure P.",
        stars: 5,
        text: "J'utilise les services de KGD depuis 2 mois pour un entretien résidentiel, ils sont bien organisés et très sérieux. Très ouvert au commentaire, on sent bien qu'ils veulent satisfaire leur client.",
    },
];

function GoogleLogo({ className = "h-5" }) {
    return (
        <svg viewBox="0 0 272 92" className={className} aria-label="Google">
            <path fill="#EA4335" d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
            <path fill="#FBBC05" d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
            <path fill="#4285F4" d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z"/>
            <path fill="#34A853" d="M225 3v65h-9.5V3h9.5z"/>
            <path fill="#EA4335" d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z"/>
            <path fill="#4285F4" d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91S16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03s10.84 25.03 24.7 25.03c8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z"/>
        </svg>
    );
}

function StarRow({ n = 5 }) {
    return (
        <div className="flex items-center gap-0.5" aria-label={`${n} étoiles sur 5`}>
            {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-4 h-4" style={{ fill: i < n ? "#F4C842" : "transparent", color: "#F4C842", strokeWidth: 1.5 }}/>
            ))}
        </div>
    );
}

function ReviewCard({ r }) {
    return (
        <article className="h-full flex flex-col rounded-2xl p-6 sm:p-7 relative" style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.03) 100%)",
            border: "1px solid rgba(212,175,55,0.28)",
            boxShadow: "0 20px 40px -24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
            backdropFilter: "blur(14px)",
        }}>
            <div className="flex items-center justify-between">
                <StarRow n={r.stars}/>
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/60">
                    <BadgeCheck className="w-3.5 h-3.5 text-[#00B8FF]"/> Avis Google vérifié
                </span>
            </div>
            <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "#E8EEF7" }}>« {r.text} »</p>
            <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-[#041126] font-serif text-sm" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37)" }}>
                    {r.name.charAt(0)}
                </div>
                <div>
                    <div className="text-sm font-medium text-white">{r.name}</div>
                    <div className="text-[11px] uppercase tracking-widest" style={{ color: "#CBD5E1" }}>Client Ménage KGD</div>
                </div>
            </div>
        </article>
    );
}

export default function GoogleReviews() {
    const [idx, setIdx] = useState(0);
    const total = REVIEWS.length;
    const trackRef = useRef(null);

    // Auto-advance every 7s on mobile
    useEffect(() => {
        const id = setInterval(() => setIdx(i => (i + 1) % total), 7000);
        return () => clearInterval(id);
    }, [total]);

    const prev = () => setIdx(i => (i - 1 + total) % total);
    const next = () => setIdx(i => (i + 1) % total);

    return (
        <section
            data-testid="google-reviews"
            className="relative overflow-hidden py-20 lg:py-28"
            style={{ background: "linear-gradient(180deg,#03102A 0%,#041126 55%,#07204A 100%)" }}
            data-theme="dark"
        >
            {/* Subtle KGD diamond halos */}
            <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,140,255,0.18), transparent 65%)" }}/>
            <div className="absolute -bottom-32 -right-24 w-[520px] h-[520px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(57,214,0,0.14), transparent 65%)" }}/>

            <div className="relative max-w-6xl mx-auto px-5 lg:px-8">
                {/* Header */}
                <div className="text-center">
                    <div className="eyebrow">Ils nous font confiance</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mt-3 curve-divider leading-[1.1] max-w-3xl mx-auto">
                        La confiance de nos clients,<br className="hidden sm:block"/> notre plus belle reconnaissance.
                    </h2>
                </div>

                {/* Rating summary */}
                <div className="mt-10 flex flex-col items-center gap-3">
                    <div className="flex items-center gap-3 rounded-full px-5 py-3" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(212,175,55,0.35)" }}>
                        <GoogleLogo className="h-5"/>
                        <span className="hidden sm:inline h-4 w-px bg-white/20"/>
                        <StarRow n={5}/>
                        <span className="text-white font-medium text-sm sm:text-base" data-testid="google-rating">{GOOGLE_RATING}</span>
                        <span className="hidden sm:inline text-white/60 text-xs">·</span>
                        <span className="hidden sm:inline text-white/70 text-xs">{GOOGLE_COUNT} avis</span>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-3">
                        <a
                            href={GOOGLE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="google-cta"
                            className="btn-ghost-gold text-sm"
                        >
                            Voir nos avis Google <ExternalLink className="w-3.5 h-3.5"/>
                        </a>
                        <a
                            href={GOOGLE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="google-share-cta"
                            className="text-sm underline-offset-4 hover:underline"
                            style={{ color: "#E8EEF7" }}
                        >
                            Partager votre expérience
                        </a>
                    </div>
                </div>

                {/* Carousel */}
                <div className="relative mt-12">
                    {/* Desktop: 3 cards visible */}
                    <div className="hidden md:grid grid-cols-3 gap-5">
                        {REVIEWS.map((r, i) => (
                            <div key={i} data-testid={`review-desktop-${i}`}>
                                <ReviewCard r={r}/>
                            </div>
                        ))}
                    </div>

                    {/* Mobile: 1 card visible, swipeable */}
                    <div className="md:hidden">
                        <div ref={trackRef} className="overflow-hidden">
                            <div
                                className="flex transition-transform duration-500 ease-out"
                                style={{ transform: `translateX(-${idx * 100}%)` }}
                            >
                                {REVIEWS.map((r, i) => (
                                    <div key={i} className="w-full flex-shrink-0 px-1" data-testid={`review-mobile-${i}`}>
                                        <ReviewCard r={r}/>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Controls */}
                        <div className="mt-6 flex items-center justify-between">
                            <button
                                onClick={prev}
                                aria-label="Avis précédent"
                                data-testid="review-prev"
                                className="w-10 h-10 rounded-full flex items-center justify-center border border-kgd-gold/40 text-white/85 hover:text-kgd-gold transition-colors"
                            >
                                <ChevronLeft className="w-4 h-4"/>
                            </button>
                            <div className="flex items-center gap-2">
                                {REVIEWS.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setIdx(i)}
                                        aria-label={`Aller à l'avis ${i + 1}`}
                                        data-testid={`review-dot-${i}`}
                                        className="w-2 h-2 rounded-full transition-all"
                                        style={{
                                            background: idx === i
                                                ? "linear-gradient(90deg,#00B8FF,#39D600)"
                                                : "rgba(255,255,255,0.25)",
                                            width: idx === i ? 20 : 8,
                                        }}
                                    />
                                ))}
                            </div>
                            <button
                                onClick={next}
                                aria-label="Avis suivant"
                                data-testid="review-next"
                                className="w-10 h-10 rounded-full flex items-center justify-center border border-kgd-gold/40 text-white/85 hover:text-kgd-gold transition-colors"
                            >
                                <ChevronRight className="w-4 h-4"/>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
